import React, { useContext, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { HiArrowLongRight, HiOutlineHeart } from "react-icons/hi2";
import { CartContext } from "./CartContext";
import productData from "../Datas/productData";
import siteConfig from "../config/siteConfig";
import "../css/main.scss";

const fallbackImage = `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 900 1200">
    <rect width="900" height="1200" fill="#e9e7e0"/>
    <path d="M330 410h240v380H330z" fill="#d2d0c8"/>
    <text x="450" y="850" text-anchor="middle" font-family="Arial" font-size="24" fill="#555">IMAGE COMING SOON</text>
  </svg>
`)}`;

const formatPrice = (price) =>
  new Intl.NumberFormat(siteConfig.locale, {
    style: "currency",
    currency: siteConfig.currency.code,
    maximumFractionDigits: 0,
  }).format(price);

const handleImageError = (event) => {
  event.currentTarget.onerror = null;
  event.currentTarget.src = fallbackImage;
};

const HomeProductCard = ({ product, favorite, onToggleWishlist }) => (
  <article className="home-product-card">
    <div className="home-product-card__media">
      <Link to={`/shopDetails/${product.id}`} aria-label={product.name}>
        <img
          src={product.image}
          alt={product.name}
          loading="lazy"
          onError={handleImageError}
        />
      </Link>
      {product.badge && (
        <span className="home-product-card__badge">{product.badge}</span>
      )}
      <button
        type="button"
        className={`home-product-card__wishlist ${favorite ? "is-active" : ""}`}
        aria-label={`${favorite ? "Remove" : "Add"} ${product.name} ${
          favorite ? "from" : "to"
        } wishlist`}
        aria-pressed={favorite}
        onClick={() => onToggleWishlist(product.id)}
      >
        <HiOutlineHeart aria-hidden="true" />
      </button>
    </div>
    <div className="home-product-card__details">
      <Link to={`/shopDetails/${product.id}`}>{product.name}</Link>
      <span>{formatPrice(product.price)}</span>
      <small>{product.colors.length} colors</small>
    </div>
  </article>
);

const editorialTiles = [
  {
    title: "Care",
    text: "Keep the clothes you love in use for longer.",
    image: "/main/mission-care.jpg",
    href: "/about#care",
  },
  {
    title: "Materials",
    text: "Better fibers, selected with purpose.",
    image: "/main/mission-materials.jpg",
    href: "/about#materials",
  },
  {
    title: "Transparency",
    text: "The people and places behind every piece.",
    image: "/main/mission-transparency.jpg",
    href: "/about#transparency",
  },
  {
    title: "People",
    text: "Long-term partnerships built on respect.",
    image: "/main/mission-people.jpg",
    href: "/about#people",
  },
  {
    title: "Repair",
    text: "Small fixes, a much longer life.",
    image: "/main/mission-repair.jpg",
    href: "/returns",
  },
];

function Main() {
  const [favoriteGender, setFavoriteGender] = useState("women");
  const { isWishlist, toggleWishlist } = useContext(CartContext);

  const favorites = useMemo(() => {
    const selectedFavorites = productData.filter(
      (product) =>
        product.gender === favoriteGender && product.tags.includes("favorite")
    );

    return selectedFavorites.length
      ? selectedFavorites
      : productData.filter((product) => product.gender === favoriteGender);
  }, [favoriteGender]);

  const scrollToTop = () => {
    if (typeof window !== "undefined") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  return (
    <main className="aa-home" id="main-content">
      <h1 className="aa-home__seo-title">
        {siteConfig.brandName} — more responsible contemporary clothing
      </h1>

      <section className="home-hero" aria-label="Shop women and men">
        <Link
          className="home-hero__panel"
          to="/shop?gender=women"
          onClick={scrollToTop}
        >
          <img
            src="/main/hero-women.jpg"
            alt="Woman wearing a relaxed responsible denim look"
            onError={handleImageError}
          />
          <span className="home-hero__scrim" aria-hidden="true" />
          <span className="home-hero__copy">
            <span>
              Shop Women <HiArrowLongRight aria-hidden="true" />
            </span>
          </span>
        </Link>

        <Link
          className="home-hero__panel"
          to="/shop?gender=men"
          onClick={scrollToTop}
        >
          <img
            src="/main/hero-men.jpg"
            alt="Man wearing a relaxed responsible everyday look"
            onError={handleImageError}
          />
          <span className="home-hero__scrim" aria-hidden="true" />
          <span className="home-hero__copy">
            <span>
              Shop Men <HiArrowLongRight aria-hidden="true" />
            </span>
          </span>
        </Link>
      </section>

      <section className="home-favorites" aria-labelledby="favorites-heading">
        <div className="home-section-heading">
          <h2 id="favorites-heading">Our favorites</h2>
          <div className="home-favorites__tabs" role="tablist" aria-label="Favorite products">
            {["women", "men"].map((gender) => (
              <button
                key={gender}
                type="button"
                role="tab"
                aria-selected={favoriteGender === gender}
                className={favoriteGender === gender ? "is-active" : ""}
                onClick={() => setFavoriteGender(gender)}
              >
                {gender}
              </button>
            ))}
          </div>
          <Link
            to={`/shop?gender=${favoriteGender}&sort=bestseller`}
            onClick={scrollToTop}
          >
            Shop all
            <HiArrowLongRight aria-hidden="true" />
          </Link>
        </div>

        <div className="home-favorites__rail">
          {favorites.map((product) => (
            <HomeProductCard
              key={product.id}
              product={product}
              favorite={isWishlist(product.id)}
              onToggleWishlist={toggleWishlist}
            />
          ))}
        </div>
      </section>

      <section className="home-campaign home-campaign--denim">
        <img
          src="/main/detoxdenim.jpg"
          alt="Responsible denim campaign in an open coastal landscape"
          loading="lazy"
          onError={handleImageError}
        />
        <div className="home-campaign__copy">
          <span>DENIM. MADE DIFFERENT.</span>
          <h2>DETOXDENIM</h2>
          <p>
            Cleaner processes, responsible fibers and timeless fits. Less impact,
            more wear.
          </p>
          <Link to="/shop?category=jeans" onClick={scrollToTop}>
            Explore denim
            <HiArrowLongRight aria-hidden="true" />
          </Link>
        </div>
      </section>

      <section className="home-impact" aria-labelledby="impact-heading">
        <div className="home-impact__media">
          <img
            src="/main/impact-report.jpg"
            alt="Hands working with responsible textile materials"
            loading="lazy"
            onError={handleImageError}
          />
        </div>
        <div className="home-impact__copy">
          <span>PROGRESS, NOT PERFECTION</span>
          <h2 id="impact-heading">Impact Report 2025</h2>
          <p>
            What we make matters. So does how we make it. Discover the goals,
            decisions and measurable steps behind our latest collection.
          </p>
          <Link to="/about#impact" onClick={scrollToTop}>
            Read our report
            <HiArrowLongRight aria-hidden="true" />
          </Link>
        </div>
      </section>

      <section className="home-mission" aria-labelledby="mission-heading">
        <span>MADE TO MAKE A DIFFERENCE</span>
        <h2 id="mission-heading">Our Mission</h2>
        <p>{siteConfig.description}</p>
        <Link to="/about" onClick={scrollToTop}>
          Learn more about us
          <HiArrowLongRight aria-hidden="true" />
        </Link>
      </section>

      <section className="home-editorial" aria-label="Explore our mission">
        {editorialTiles.map((tile, index) => (
          <Link
            className={`home-editorial__tile home-editorial__tile--${index + 1}`}
            key={tile.title}
            to={tile.href}
            onClick={scrollToTop}
          >
            <img
              src={tile.image}
              alt={`${tile.title} at ${siteConfig.brandName}`}
              loading="lazy"
              onError={handleImageError}
            />
            <span className="home-editorial__overlay" aria-hidden="true" />
            <span className="home-editorial__copy">
              <strong>{tile.title}</strong>
              <span>{tile.text}</span>
              <em>
                Discover <HiArrowLongRight aria-hidden="true" />
              </em>
            </span>
          </Link>
        ))}
      </section>
    </main>
  );
}

export default Main;
